<template>
  <div class="pa12-model-list">
    <div class="header">
      <h3>
        SLS 모델 리스트
        <span v-if="models.length > 0" class="text-xs text-gray-300"
          >(총 {{ models.length }}개)</span
        >
      </h3>

      <button @click="loadPa12Models" class="refresh-btn" :disabled="loading">
        {{ loading ? "로딩중..." : "새로고침" }}
      </button>
    </div>

    <div v-if="loading" class="loading">SLS 모델을 불러오는 중...</div>

    <div v-else-if="error" class="error">
      {{ error }}
    </div>

    <div v-else-if="models.length === 0" class="empty">SLS 모델이 없습니다.</div>

    <div v-else class="model-list">
      <div
        v-for="model in models"
        :key="model.id"
        class="model-item"
        @click="selectModel(model)"
        :class="{selected: selectedModel && selectedModel.id === model.id}"
      >
        <div class="model-info">
          <div class="model-name">
            <span class="color-dot" :style="{backgroundColor: getModelColor(model.name)}"></span>
            {{ model.name }}
          </div>
          <div class="model-details">
            <span>수량: {{ model.modelCount }}개</span>
            <span>부피: {{ model.modelVolume }}cm³</span>
          </div>
          <div class="model-dimensions">
            크기: {{ model.modelSizeX.toFixed(1) }} x {{ model.modelSizeY.toFixed(1) }} x
            {{ model.modelSizeZ.toFixed(1) }}mm
          </div>
        </div>
        <div class="model-actions">
          <button @click.stop="addToPacking(model)" class="add-btn" title="패킹에 추가 (수량만큼)">
            📦
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted} from "vue";
import axios from "../../js/axios";

const props = defineProps({
  estimateId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["model-selected", "add-to-packing"]);

const models = ref([]);
const loading = ref(false);
const error = ref("");
const selectedModel = ref(null);

// 전역 색상 관리 시스템 사용
function getModelColor(modelName) {
  if (window.globalColorManager) {
    return window.globalColorManager.getModelColor(modelName);
  }
  // fallback: 기본 색상
  return "#ff6347";
}

function assignColorToModel(modelName) {
  if (window.globalColorManager) {
    return window.globalColorManager.assignColorToModel(modelName);
  }
  // fallback: 기본 색상
  return 0xff6347;
}

const loadPa12Models = async () => {
  if (!props.estimateId) {
    error.value = "견적서 ID가 필요합니다.";
    return;
  }

  loading.value = true;
  error.value = "";

  try {
    const response = await axios.get(`/estimates/${props.estimateId}/pa12-models`);
    models.value = response.data.models || [];
  } catch (err) {
    console.error("PA12 모델 로드 실패:", err);
    error.value = "PA12 모델을 불러오는데 실패했습니다.";
  } finally {
    loading.value = false;
  }
};

const selectModel = (model) => {
  selectedModel.value = model;
  emit("model-selected", model);
};

const addToPacking = (model) => {
  // 색상 할당
  const color = assignColorToModel(model.name);

  // 수량만큼 박스를 추가
  for (let i = 0; i < model.modelCount; i++) {
    emit("add-to-packing", {
      name: `${model.name}`,
      count: 1,
      dimensions: [model.modelSizeX, model.modelSizeY, model.modelSizeZ],
      volume: model.modelVolume,
      color: color, // 색상 정보 추가
    });
  }
  // toast.success(`${model.name} 모델이 ${model.modelCount}개 박스로 추가되었습니다!`, {
  //   position: toast.POSITION.TOP_CENTER,
  // });
};

onMounted(() => {
  loadPa12Models();
});
</script>

<style scoped>
.pa12-model-list {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dee2e6;
}

.header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.refresh-btn {
  padding: 5px 9px;
  font-size: 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

/* .refresh-btn:hover:not(:disabled) {
  background: #0056b3;
} */

.refresh-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.loading,
.error,
.empty {
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 13px;
}

.error {
  color: #dc3545;
}

.model-list {
  /* max-height: 300px;
  overflow-y: auto; */
}

.model-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  margin-bottom: 6px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.model-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.model-item.selected {
  border-color: #007bff;
  background: #f8f9ff;
}

.model-info {
  flex: 1;
}

.model-name {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #ccc;
  flex-shrink: 0;
}

.model-details {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
}

.model-dimensions {
  font-size: 11px;
  color: #888;
}

.model-actions {
  display: flex;
  gap: 4px;
}

.add-btn {
  padding: 6px 6px;
  background: #6abcd3;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
}

.add-btn:hover {
  background: #5dabc1;
}
</style>
